import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { authLogger } from '@/utils/logger';
import { toError } from '@/utils/toError';
import { toast } from 'sonner';
import i18n from '@/lib/i18n';
import { setUserContext } from '@/lib/sentry';
import { Analytics } from '@/lib/analytics';

interface SubscriptionData {
  subscribed: boolean;
  subscription_tier?: string;
  subscription_end?: string;
  trial_expired?: boolean;
  trial_ends_at?: string | null;
  is_manual_premium?: boolean;
  email?: string;
}

interface CachedSubscription {
  data: SubscriptionData;
  timestamp: number;
}

const SUBSCRIPTION_CACHE_KEY = 'subscription_cache';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  subscription: SubscriptionData | null;
  isLoadingSubscription: boolean;
  signOut: () => Promise<void>;
  checkSubscription: () => Promise<void>;
  canUseFeature: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  // CRITICAL: Commencer à true pour éviter le flash d'upgrade prompt avant que le statut soit vérifié
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
  // Deduplication guard: prevents concurrent checkSubscription calls
  const isCheckingSubscriptionRef = useRef(false);
  const checkPromiseRef = useRef<Promise<void> | null>(null);

  const getCachedSubscription = (): SubscriptionData | null => {
    try {
      const cached = localStorage.getItem(SUBSCRIPTION_CACHE_KEY);
      if (!cached) return null;
      
      const { data, timestamp }: CachedSubscription = JSON.parse(cached);
      const now = Date.now();
      
      // Cache expiré
      if (now - timestamp > CACHE_TTL) {
        localStorage.removeItem(SUBSCRIPTION_CACHE_KEY);
        return null;
      }
      
      return data;
    } catch {
      return null;
    }
  };

  const setCachedSubscription = (data: SubscriptionData) => {
    try {
      localStorage.setItem(SUBSCRIPTION_CACHE_KEY, JSON.stringify({
        data,
        timestamp: Date.now()
      }));
    } catch {
      // Ignore cache errors
    }
  };

  const checkSubscription = async (accessToken?: string) => {
    // Deduplication: if already checking, return existing promise
    if (isCheckingSubscriptionRef.current && checkPromiseRef.current) {
      authLogger.log('checkSubscription already in progress, deduplicating');
      return checkPromiseRef.current;
    }
    
    isCheckingSubscriptionRef.current = true;
    const doCheck = async () => {
      try {
        await _checkSubscriptionImpl(accessToken);
      } finally {
        isCheckingSubscriptionRef.current = false;
        checkPromiseRef.current = null;
      }
    };
    
    checkPromiseRef.current = doCheck();
    return checkPromiseRef.current;
  };

  const _checkSubscriptionImpl = async (accessToken?: string) => {
    let token = accessToken || session?.access_token;
    if (!token) {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session?.access_token) {
        authLogger.log('No valid session or token, cannot check subscription');
        setIsLoadingSubscription(false);
        return;
      }
      token = sessionData.session.access_token;
    }
    
    authLogger.log('Checking subscription status...');
    setIsLoadingSubscription(true);
    try {
      const { data, error } = await supabase.functions.invoke('check-subscription', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (error) {
        authLogger.error('Subscription check error:', toError(error).message);
        
        // Handle 401 errors (expired/invalid session) - BUT NOT during auth flows
        if (error.message?.includes('Authentication failed') || error.message?.includes('Invalid session')) {
          // GUARD: Don't sign out if we're on /auth with recovery/email_change flow
          const isAuthFlow = window.location.pathname === '/auth' && 
            (window.location.search.includes('type=recovery') || 
             window.location.search.includes('type=email_change') ||
             window.location.hash.includes('type=recovery') ||
             window.location.hash.includes('type=email_change') ||
             window.location.hash.includes('access_token'));
          
          if (isAuthFlow) {
            authLogger.log('Skipping signOut during auth flow (recovery/email_change)');
            setIsLoadingSubscription(false);
            return;
          }
          
          authLogger.log('Session expired - forcing sign out');
          await signOut();
          return;
        }
        
        // If it's a 404 "User not found" error, try session refresh
        if (error.message?.includes('User not found')) {
          authLogger.log('User not found - attempting session refresh');
          const { error: refreshError } = await supabase.auth.refreshSession();
          if (!refreshError) {
            // Retry after refresh
            const { data: retryData, error: retryError } = await supabase.functions.invoke('check-subscription');
            if (!retryError && retryData) {
              setSubscription(retryData);
              setCachedSubscription(retryData);
              setIsLoadingSubscription(false);
              return;
            }
          }
        }
        
        // Try to use cached data (fail-open with cache)
        const cached = getCachedSubscription();
        if (cached) {
          authLogger.log('Using cached subscription data (fail-open with cache)');
          setSubscription(cached);
          setIsLoadingSubscription(false);
          return;
        }
        
        // Fail-closed if no cache available
        authLogger.error('SECURITY: Edge function failed - denying access (fail-closed)');
        setSubscription({ 
          subscribed: false, 
          trial_expired: true,
          is_manual_premium: false
        });
        setIsLoadingSubscription(false);
        return;
      }
      authLogger.log('Subscription data received:', data);
      setSubscription(data);
      setCachedSubscription(data);
      // Update PostHog user properties with subscription state
      Analytics.setUserProperties({
        subscription_tier: data.subscription_tier || 'free',
        is_subscribed: data.subscribed,
        trial_expired: data.trial_expired || false,
        is_premium: data.is_manual_premium || false,
      });
    } catch (error) {
      const err = toError(error);
      authLogger.error('Error checking subscription:', err.message);
      
      // Handle 401 errors globally
      if (err.message?.includes('401') || (error as any)?.status === 401) {
        authLogger.log('401 error detected - session expired, signing out');
        await signOut();
        return;
      }
      
      // Try cached data first (fail-open)
      const cached = getCachedSubscription();
      if (cached) {
        authLogger.log('Using cached subscription data after error (fail-open)');
        setSubscription(cached);
      } else {
        // Fail-closed if no cache
        authLogger.error('Subscription check failed, applying fail-closed policy:', toError(error).message);
        setSubscription({ 
          subscribed: false, 
          trial_expired: true,
          is_manual_premium: false
        });
      }
    } finally {
      setIsLoadingSubscription(false);
    }
  };

  const checkAdminStatus = async () => {
    // Admin check removed for simplified security model
    // Use server-side role checks instead
  };

  const canUseFeature = () => {
    // Fail-open pendant le chargement : utiliser le cache pour ne pas bloquer les premium
    if (isLoadingSubscription) {
      const cached = getCachedSubscription();
      if (cached?.is_manual_premium || cached?.subscribed) return true;
      return false;
    }
    
    // Use subscription state, or fallback to cache if null
    const sub = subscription ?? getCachedSubscription();
    if (!sub) return false;
    
    try {
      // 1. Manual premium: toujours autorisé (vérifié server-side)
      if (sub.is_manual_premium === true) {
        return true;
      }
      
      // 2. Subscription payante: vérifier la date de fin
      if (sub.subscribed === true && sub.subscription_end) {
        const now = Date.now();
        const endDate = new Date(sub.subscription_end).getTime();
        return now < endDate;
      }
      
      // 3. Trial: vérifier UNIQUEMENT le temps, pas le flag
      if (sub.trial_ends_at) {
        const now = Date.now();
        const trialEnd = new Date(sub.trial_ends_at).getTime();
        return now < trialEnd;
      }
      
      // Default: deny access
      return false;
    } catch (error) {
      authLogger.error('Access check failed:', toError(error).message);
      return false;
    }
  };

  useEffect(() => {
    // Set up auth state listener with retry logic
    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        authLogger.log(`Auth state change: ${event}`, { hasSession: !!session });
        
        setSession(session);
        setUser(session?.user ?? null);
        
        if (event === 'SIGNED_IN' && session?.access_token) {
          authLogger.log('SIGNED_IN with valid token - checking subscription');
          Analytics.identify(session.user.id, {
            email: session.user.email,
            created_at: session.user.created_at,
          });
          // Handle subscription check with retry
          let retries = 0;
          const maxRetries = 3;
          
          const attemptCheck = async () => {
            try {
              await checkSubscription(session.access_token);
              await checkAdminStatus();
            } catch (error) {
              authLogger.error('Subscription check failed:', toError(error).message);
              if (retries < maxRetries) {
                retries++;
                authLogger.log(`Retrying subscription check (${retries}/${maxRetries})`);
                setTimeout(attemptCheck, 1000 * retries);
              }
            }
          };
          
          attemptCheck();
        } else if (event === 'SIGNED_OUT') {
          authLogger.log('SIGNED_OUT - clearing subscription state');
          Analytics.reset();
          setSubscription(null);
          localStorage.removeItem(SUBSCRIPTION_CACHE_KEY);
        } else if (event === 'TOKEN_REFRESHED' && session?.access_token) {
          authLogger.log('TOKEN_REFRESHED - re-checking subscription (not clearing)');
          checkSubscription(session.access_token);
        }
        
        setIsLoading(false);
      }
    );

    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);
      
      // Check subscription and admin status for existing session
      if (session) {
        authLogger.log('Initial session found - calling checkSubscription');
        setTimeout(() => {
          checkSubscription(session.access_token);
          checkAdminStatus();
          // Auto-register push notifications for existing sessions
          if ('serviceWorker' in navigator && 'PushManager' in window) {
            import('@/hooks/usePushNotifications').then(({ enablePushNotificationsAutomatically }) => {
              enablePushNotificationsAutomatically?.();
            });
          }
        }, 100);
      } else {
        // Pas de session = pas de subscription à charger
        setIsLoadingSubscription(false);
      }
    });

    return () => authSubscription.unsubscribe();
  }, []);

  // REMOVED: Redundant useEffect that duplicated subscription check from onAuthStateChange + getSession

  // Sentry: set user context on change
  useEffect(() => {
    try {
      if (user) {
        const data: { id: string; email?: string; role?: string } = { id: user.id };
        if (user.email) data.email = user.email as string;
        setUserContext(data);
      } else {
        setUserContext({ id: 'anonymous' });
      }
    } catch (e) {
      // silent fail
    }
  }, [user]);

  // Vérification périodique de l'expiration d'essai (PHASE 3: réduit à 5 minutes)
  useEffect(() => {
    if (!session || !subscription || subscription.subscribed) return;

    let timeoutId: ReturnType<typeof setTimeout>;
    
    const checkTrialExpiry = () => {
      if (subscription.trial_ends_at) {
        const trialEndTime = new Date(subscription.trial_ends_at).getTime();
        const now = Date.now();
        const isNowExpired = now > trialEndTime;
        
        // Ne rafraîchir QUE si le statut a réellement changé
        if (isNowExpired && canUseFeature()) {
          authLogger.log('Trial vient d\'expirer - refreshing subscription');
          checkSubscription();
        }
      }
    };

    // Debounce: attendre 1 seconde avant de vérifier
    timeoutId = setTimeout(() => {
      checkTrialExpiry();
      // Vérifier toutes les 5 minutes (au lieu de 30 secondes)
      const interval = setInterval(checkTrialExpiry, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }, 1000);
    
    return () => clearTimeout(timeoutId);
  }, [session, subscription]);

  const signOut = async (): Promise<void> => {
    try {
      setIsLoading(true);
      authLogger.log('Starting sign out process');
      
      // Clear local storage before API call
      localStorage.removeItem('supabase.auth.token');
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('sb-tkjekphvwwzmhfoondwr-auth-token')) {
          localStorage.removeItem(key);
        }
      });
      
      // Clear state immediately
      setUser(null);
      setSession(null);
      setSubscription(null);
      
      // Attempt global sign out
      const { error } = await supabase.auth.signOut({ scope: 'global' });
      
      if (error) {
        authLogger.error('Sign out API error:', toError(error).message);
        // Continue even if API fails
      }
      
      authLogger.log('Sign out completed, redirecting...');
      
      // Force reload for complete cleanup - even if already on /auth
      if (window.location.pathname === '/auth') {
        authLogger.log('Already on /auth, forcing page reload...');
        window.location.reload();
      } else {
        authLogger.log('Redirecting to /auth...');
        window.location.href = '/auth';
      }
    } catch (error) {
      authLogger.error('Sign out error:', toError(error).message);
      toast.error(i18n.t('common.profile.signoutError'));
      // Still force reload/redirect on error
      if (window.location.pathname === '/auth') {
        window.location.reload();
      } else {
        window.location.href = '/auth';
      }
    }
  };

  const value = {
    user,
    session,
    isLoading,
    subscription,
    isLoadingSubscription,
    signOut,
    checkSubscription,
    canUseFeature,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};